import React, { useState, useEffect, useCallback } from "react";
import DropdownNew, { DropdownOption } from "./Dropdown";
import CalendarIcon from "../../../assets/images/DateGray.svg";
import { CgClose } from "react-icons/cg";
import Input from "./Input";
import Button from "react-bootstrap/Button";
import { useAuth } from "../../../firebase/AuthProvider";
import { getTasks } from "../../../services/taskServices/TaskService";
import TaskList from "../../../interfaces/task/TaskList";
import ActionSubType from "../../../interfaces/task/taskActions/ActionSubType";
import { getTaskActionSubTypes } from "../../../services/taskServices/TaskActionService";
import TemplateFile from "../../../interfaces/task/TaskFiles/TemplateFile";
import {
  getTemplateFiles,
  uploadFile,
} from "../../../services/fileServices/FileService";
import { toast } from "react-toastify";
import { createTaskAction } from "../../../services/taskServices/TaskActionService";
import TaskActionRequest from "../../../interfaces/task/taskActions/TaskActionRequest";
import { useNavigate } from "react-router-dom";

interface LogActivityModalProps {
  onClose: () => void;
  onActivityCreated?: () => void;
  selectedParticipantId?: number | null;
}

export default function LogActivityModal({
  onClose,
  onActivityCreated,
  selectedParticipantId
}: LogActivityModalProps) {
  const { currentUser, currentOrganization } = useAuth();
  const navigate = useNavigate();

  const CloseIcon = CgClose as React.ComponentType;

  // State for tasks
  const [tasks, setTasks] = useState<TaskList[]>([]);
  const [taskOptions, setTaskOptions] = useState<DropdownOption[]>([]);
  const [taskId, setTaskId] = useState<string | number>("");
  const [taskSearchTerm, setTaskSearchTerm] = useState("");
  const [isTasksLoading, setIsTasksLoading] = useState(false);

  // State for action types
  const [actionTypes, setActionTypes] = useState<ActionSubType[] | null>(null);
  const [actionTypeOptions, setActionTypeOptions] = useState<DropdownOption[]>(
    []
  );
  const [activityType, setActivityType] = useState<string | number>("");

  // State for form fields
  const [hours, setHours] = useState("0");
  const [minutes, setMinutes] = useState("15");
  const [completionDate, setCompletionDate] = useState("");
  const [completionTime, setCompletionTime] = useState("");
  const [workDone, setWorkDone] = useState("");

  // State for attachments
  const [attachmentRequirements, setAttachmentRequirements] =
    useState<TemplateFile[]>();
  const [selectedAttachment, setSelectedAttachment] = useState<
    TemplateFile | undefined
  >(undefined);

  // State for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // State for immutability
  const [descriptionImmutable, setDescriptionImmutable] =
    useState<boolean>(false);
  const [recommendedTimeImmutable, setRecommendedTimeImmutable] =
    useState<boolean>(false);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const today = new Date().toISOString().split("T")[0];

  const labelsToFilterOut = [
    "COMMENT",
    "TASK FILE DELETED",
    "FILE UPLOAD",
    "TASK APPROVAL",
    "TASK SUBMISSION",
    "TASK COMPLETED",
    "REJECTED",
  ];

  // Initialize dates
  useEffect(() => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    setCompletionDate(`${year}-${month}-${day}`);
    const currentHour = String(currentDate.getHours()).padStart(2, "0");
    const currentMinute = String(currentDate.getMinutes()).padStart(2, "0");
    setCompletionTime(`${currentHour}:${currentMinute}`);
  }, []);

  // --- Dynamic Task Fetching ---
  const fetchTasks = useCallback(async (search: string = "") => {
    if (!currentUser) return;

    setIsTasksLoading(true);

    try {
        const authToken = await currentUser.getIdToken();
        
        // Filter by selectedParticipantId if present
        const filters = selectedParticipantId 
            ? { participants: [selectedParticipantId.toString()] }
            : null;

        const tasksResponse = await getTasks(
            authToken,
            undefined, // userId
            1, // page
            100, // pageSize
            filters, 
            "", // sortBy
            false, // sortDesc
            false, // viewAll
            false, // includePendingApprovals
            search // searchTerm
        );

        if (tasksResponse) {
            setTasks(tasksResponse.tasks);
            const formattedTasks = tasksResponse.tasks.map((task) => ({
                value: task.id.toString(),
                label: `(${currentOrganization?.abbreviation}-${task.id}) ${task.title}`,
            }));
            setTaskOptions(formattedTasks);
            
            // Auto-select if only 1 task is returned and we are filtering by participant
            // This is a nice UX touch for participants with only 1 case plan
            if (selectedParticipantId && tasksResponse.tasks.length === 1 && !taskId) {
                setTaskId(tasksResponse.tasks[0].id.toString());
            }
        } else {
            setTaskOptions([]);
        }
    } catch (error) {
        console.error("Error fetching tasks:", error);
        toast.error("Failed to load tasks");
        setTaskOptions([]);
    } finally {
        setIsTasksLoading(false);
    }
  }, [currentUser, currentOrganization, selectedParticipantId, taskId]);

  // Debounce task search
  useEffect(() => {
    const timer = setTimeout(() => {
        fetchTasks(taskSearchTerm);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [taskSearchTerm, fetchTasks]);

  // Fetch static data (Action Types & Attachments)
  useEffect(() => {
    const fetchStaticData = async () => {
      if (currentUser) {
        try {
          const authToken = await currentUser.getIdToken();

          // Fetch action types
          const actionTypesResponse = await getTaskActionSubTypes(authToken);
          if (actionTypesResponse) {
            const filteredOptions = actionTypesResponse.filter(
              (option) => !labelsToFilterOut.includes(option.actionSubTypeName)
            );
            setActionTypes(filteredOptions);
            const formattedActionTypes = filteredOptions.map((at) => ({
              value: at.id.toString(),
              label: at.actionSubTypeName,
            }));
            setActionTypeOptions(formattedActionTypes);
          }

          // Fetch attachment requirements
          const attachmentsResponse = await getTemplateFiles(authToken);
          if (attachmentsResponse) {
            setAttachmentRequirements(attachmentsResponse);
          }
        } catch (error) {
          console.error("Error fetching static data:", error);
          toast.error("Failed to load activity types");
        }
      }
    };

    fetchStaticData();
  }, [currentUser]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
    if (formErrors.attachment) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.attachment;
        return newErrors;
      });
    }
  };

  const handleActionTypeChange = (value: string | number) => {
    setActivityType(value);

    const actionSubType = actionTypes?.find(
      (at) => at.id === parseInt(value.toString(), 10)
    );

    if (actionSubType?.description) {
      setDescriptionImmutable(true);
      setWorkDone(actionSubType.description);
    } else {
      setDescriptionImmutable(false);
      setWorkDone("");
    }

    if (actionSubType?.recommendedTimeInMinutes) {
      setRecommendedTimeImmutable(true);
      const recommendedMinutes = actionSubType.recommendedTimeInMinutes;
      const hrs = Math.floor(recommendedMinutes / 60);
      const mins = recommendedMinutes % 60;
      setHours(hrs.toString());
      setMinutes(mins.toString());
    } else {
      setRecommendedTimeImmutable(false);
      setHours("0");
      setMinutes("0");
    }

    if (actionSubType?.requiresFile) {
      const attachment = attachmentRequirements?.find(
        (attachment) => attachment.id === Number(actionSubType.requiresFile)
      );
      setSelectedAttachment(attachment);
    } else {
      setSelectedAttachment(undefined);
    }

    if (formErrors.activityType) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.activityType;
        return newErrors;
      });
    }
  };

  const handleTaskChange = (value: string | number) => {
    setTaskId(value);
    if (formErrors.taskId) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.taskId;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!taskId) errors.taskId = "Task is required.";
    if (!activityType) errors.activityType = "Activity type is required.";
    if (
      (!hours || parseInt(hours) === 0) &&
      (!minutes || parseInt(minutes) === 0)
    )
      errors.timeSpent = "Time spent is required.";
    if (hours && isNaN(Number(hours)))
      errors.timeSpent = "Hours must be a valid number.";
    if (minutes && isNaN(Number(minutes)))
      errors.timeSpent = "Minutes must be a valid number.";
    if (Number(minutes) >= 60)
      errors.timeSpent = "Minutes must be less than 60.";
    if (!completionDate) errors.completionDate = "Completion date is required.";
    if (!completionTime) errors.completionTime = "Completion time is required.";
    if (!workDone) errors.workDone = "Work done description is required.";
    if (selectedAttachment && !selectedFile) {
      errors.attachment = "Please select a file to upload";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix validation errors before submitting");
      return;
    }

    if (!currentUser) {
      toast.error("Authentication error. Please log in again.");
      return;
    }

    setIsSubmitting(true);

    try {
      const authToken = await currentUser.getIdToken();
      const totalMinutes =
        (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);

      const actionData: TaskActionRequest = {
        taskId: taskId.toString(),
        description: workDone,
        subTypeId: activityType.toString(),
        durationInMinutes: totalMinutes.toString(),
        completionDate: completionDate,
        completionTime: completionTime,
        requiresFile: selectedAttachment
          ? selectedAttachment.id.toString()
          : null,
      };

      if (selectedAttachment && !selectedFile) {
        toast.error("Please select a file to upload");
        setIsSubmitting(false);
        return;
      }

      if (selectedFile) {
        const fileToUpload = selectedAttachment
          ? new File(
            [selectedFile],
            selectedAttachment.fileName, 
            { type: selectedFile.type }
          )
          : selectedFile;

        const fileSize = fileToUpload.size;
        const mimeType = fileToUpload.type || "application/octet-stream";

        const uploadSuccess = await uploadFile(
          fileToUpload,
          taskId.toString(),
          mimeType,
          fileSize,
          authToken
        );

        if (!uploadSuccess) {
          toast.error("File upload failed");
          setIsSubmitting(false);
          return;
        }
      }

      const result = await createTaskAction(authToken, actionData);

      if (result) {
        if (onActivityCreated) {
          onActivityCreated();
        }
        onClose();
        toast.success("Activity logged successfully!");
        const friendlyId = tasks.filter((t) => t.id == taskId)[0].friendlyId
        navigate(
          `/task/${currentOrganization?.abbreviation}-${friendlyId}?focusedActivity=${result}`
        );
      } else {
        toast.error("Failed to log activity");
      }
    } catch (error) {
      console.error("Error logging activity:", error);
      toast.error("Failed to log activity. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
            Log Activity
            {selectedParticipantId && <span className="text-sm font-normal text-gray-500 ml-2">(Filtered by Selected Participant)</span>}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-5">
          <DropdownNew
            label="Task By ID"
            name="taskId"
            value={taskId}
            onChange={handleTaskChange}
            options={taskOptions}
            placeholder={isTasksLoading ? "Searching tasks..." : "Select task"}
            error={formErrors.taskId}
            // Pass the new props for searching
            onSearchChange={(term) => setTaskSearchTerm(term)}
            isLoading={isTasksLoading}
          />

          <DropdownNew
            label="Activity Type"
            name="activityType"
            value={activityType}
            onChange={handleActionTypeChange}
            options={actionTypeOptions}
            placeholder="Select activity type"
            error={formErrors.activityType}
          />

          <div className="flex space-x-4">
            <div className="w-1/2">
              <Input
                label="Hours"
                name="hours"
                value={hours}
                placeholder="0"
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value === "" ||
                    (/^\d+$/.test(value) && parseInt(value) >= 0)
                  ) {
                    setHours(value);
                  }
                  if (formErrors.timeSpent) {
                    setFormErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.timeSpent;
                      return newErrors;
                    });
                  }
                }}
                error={formErrors.timeSpent}
              />
            </div>
            <div className="w-1/2">
              <Input
                label="Minutes"
                name="minutes"
                value={minutes}
                placeholder="0"
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value === "" ||
                    (/^\d+$/.test(value) &&
                      parseInt(value) >= 0 &&
                      parseInt(value) < 60)
                  ) {
                    setMinutes(value);
                  }
                  if (formErrors.timeSpent) {
                    setFormErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.timeSpent;
                      return newErrors;
                    });
                  }
                }}
                error={formErrors.timeSpent}
              />
            </div>
          </div>

          <div className="flex gap-x-4">
            <div className="w-1/2">
              <Input
                type="date"
                label="Completion Date"
                name="completionDate"
                value={completionDate}
                max={today}
                onChange={(e) => {
                  setCompletionDate(e.target.value);
                  if (formErrors.completionDate) {
                    setFormErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.completionDate;
                      return newErrors;
                    });
                  }
                }}
                icon={CalendarIcon}
                error={formErrors.completionDate}
              />
            </div>

            <div className="w-1/2">
              <Input
                type="time"
                label="Completion Time"
                name="completionTime"
                value={completionTime}
                onChange={(e) => {
                  setCompletionTime(e.target.value);
                  if (formErrors.completionTime) {
                    setFormErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.completionTime;
                      return newErrors;
                    });
                  }
                }}
                error={formErrors.completionTime}
              />
            </div>
          </div>

          <div>
            <Input
              label="Work Done"
              type="textarea"
              name="workDone"
              value={workDone}
              placeholder="Describe the work completed or progress made..."
              onChange={(e) => setWorkDone(e.target.value)}
              error={formErrors.workDone}
            />
          </div>

          <div className="space-y-1.5 w-full">
            <label className="text-sm font-medium text-[#101828] flex items-center gap-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5 text-[#0A0A0A]"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M18.375 12.739l-7.5 7.5a4.125 4.125 0 01-5.836-5.836l7.5-7.5a2.75 2.75 0 013.889 3.889l-7.5 7.5a1.375 1.375 0 01-1.945-1.945l7.5-7.5"
                />
              </svg>
              Attachment{" "}
              {selectedAttachment && <span className="text-red-500">*</span>}
            </label>

            <label
              htmlFor="fileUpload"
              className="w-full rounded-xl px-4 py-3 text-base text-[#717182] flex items-center justify-center gap-2 cursor-pointer"
              style={{
                border: formErrors.attachment
                  ? "1px dashed red"
                  : "1px dashed #e5e5e5",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-6 h-6 text-[#C9C9C9]"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M18.375 12.739l-7.5 7.5a4.125 4.125 0 01-5.836-5.836l7.5-7.5a2.75 2.75 0 013.889 3.889l-7.5 7.5a1.375 1.375 0 01-1.945-1.945l7.5-7.5"
                />
              </svg>

              <span className="text-[#717182]">
                {selectedFile ? selectedFile.name : "Choose a file"}
              </span>
            </label>

            <input
              id="fileUpload"
              type="file"
              className="hidden"
              accept=".jpg,.jpeg,.png,.gif,.pdf"
              onChange={handleFileChange}
            />

            {selectedAttachment && (
              <div className="mt-2 text-sm text-gray-600">
                Required File:{" "}
                <span className="bg-gray-100 p-1 italic">
                  {selectedAttachment.fileName}
                </span>
              </div>
            )}
          </div>

          <div className="modal_footer border-t pt-3 flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Logging..." : "Log Activity"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}